/* eslint-disable no-console */
import React, { useContext, useState } from 'react';
import { Box, TextField, Button, CircularProgress, Typography } from '@mui/material';
import {
  ArchivedState,
  CreateTeamMemberInput, PersonType, Role,
} from 'API';
import { useImmer } from 'use-immer';
import { signOut as amplifySignOut } from 'aws-amplify/auth';
import AuthService from 'services/auth';
import PersonService from 'services/person';
import slugify from 'slugify';
import UserContext from 'shared/contexts/UserContext';
import { isValidEmail } from 'shared/utilities';

const initialInput = {
  slug: '',
  email: '',
  firstname: '',
  lastname: '',
  type: PersonType.TeamMember,
  archived: ArchivedState.ACTIVE,
  lowerFirst: '-',
  lowerLast: '-',
  lowerFull: '-',
};

const AddAdmin: React.FC = () => {
  const [
    personInput, setPersonInput,
  ] = useImmer<CreateTeamMemberInput>(initialInput);
  const [userState] = useContext(UserContext);
  const { role, user } = userState;
  const [isLoading, setIsLoading] = useState(false);
  const [, authDispatch] = useContext(UserContext);

  // In v6, username IS the email (Cognito username attribute is EMAIL)
  const userEmail = user?.username ?? '';

  const signOut = async () => {
    try {
      authDispatch({ type: 'logout' });
      await amplifySignOut();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('error signing out: ', error);
    }
  };

  const handleChange = (event: any) => {
    setPersonInput((draft) => {
      const name = event.target.name as 'slug' | 'firstname' | 'lastname' | 'email';
      draft[name] = event.target.value;
      draft.slug = slugify(`${personInput.firstname} ${personInput.lastname}`);
    });
  };

  const handleSave = async () => {
    if (!personInput.email) {
      return;
    }

    setIsLoading(true);

    try {
      const newPerson = await PersonService.create(personInput, Role.ADMIN);

      if (newPerson) {
        await AuthService.createUser({
          personID: newPerson.id,
          role: Role.ADMIN,
          email: personInput.email,
        });
      }
      setPersonInput(initialInput);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  return personInput && user && role === Role.ADMIN ? (
    <Box sx={{ maxWidth: '40rem', padding: '2rem' }} display="flex" flexDirection="column">
      <Typography sx={{ marginBottom: '2rem' }}>
        {`You are at this screen because you are an admin, but there is no
        profile attached to your user account. You will need to create a new
        admin user, on behalf of yourself or someone else, in order to
        interact with the platform. A profile will be created for this new
        user, and a temporary password will be emailed to them.`}
      </Typography>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <TextField
            sx={{ marginBottom: '1rem' }}
            value={personInput.firstname}
            label="First name"
            name="firstname"
            onChange={handleChange}
          />
          <TextField
            sx={{ marginBottom: '1rem' }}
            value={personInput.lastname}
            label="Last name"
            name="lastname"
            onChange={handleChange}
          />
          <TextField
            sx={{ marginBottom: '1rem' }}
            value={personInput.email}
            label="Email"
            name="email"
            onChange={handleChange}
            error={personInput.email === userEmail}
            helperText={personInput.email === userEmail
              ? `The new user's email must be different from the one
                 you used to log in.`
              : ''}
          />
          <Button
            color="primary"
            onClick={handleSave}
            variant="contained"
            disabled={
              !isValidEmail(personInput.email || '')
              || !personInput.firstname
              || !personInput.lastname
              || personInput.email === userEmail
            }
            style={{ marginBottom: '2rem' }}
          >
            Create user
          </Button>
          <Button onClick={signOut} color="secondary">
            Sign out
          </Button>
        </>
      )}
    </Box>
  ) : (
    <Box sx={{ maxWidth: '40rem', padding: '2rem' }}>
      <Typography paragraph>
        There was a problem retrieving your profile. Please
        log out and attempt to log in again.
      </Typography>

      <Button
        onClick={signOut}
        variant="contained"
        color="primary"
      >
        Log out

      </Button>
    </Box>
  );
};

export default AddAdmin;
